import type { AppRouterContext } from "~/lib/router-runtime";
import { SymbolPagePending } from "~/features/symbol/routing/symbol-page-pending";
import { createSocialHeadMeta } from "~/lib/social-head";
import { createSymbolSocialMetadata } from "~/lib/social-metadata";
import {
  buildCanonicalSymbolHrefWithClass,
  parseCanonicalSymbolRouteParams,
  type SymbolRouteAssetClass,
  CANONICAL_SECTION_REGISTRY,
} from "../symbol-route-params";

export function createCanonicalSymbolRouteOptions(
  assetClass: SymbolRouteAssetClass,
) {
  const routeConfig = CANONICAL_SECTION_REGISTRY[assetClass];

  return {
    params: {
      parse: parseCanonicalSymbolRouteParams,
    },
    // Paint the page silhouette inside the live chrome the moment navigation
    // commits (no 1s default delay) while the lazy chunk loads. The ticker
    // from the URL renders as real content immediately.
    pendingComponent: SymbolPagePending,
    pendingMs: 0,
    head: ({ params }: { params: { symbol: string } }) => ({
      links: [
        {
          href:
            buildCanonicalSymbolHrefWithClass(params.symbol, assetClass) ??
            `/${routeConfig.section}/${encodeURIComponent(params.symbol)}`,
          rel: "canonical",
        },
      ],
      meta: createSocialHeadMeta(
        createSymbolSocialMetadata(params.symbol, {
          assetClass: routeConfig.openGraphAssetClass,
        }),
      ),
    }),
    loader: ({
      context,
      params,
      preload,
    }: {
      context: AppRouterContext;
      params: { symbol: string };
      preload: boolean;
    }) => {
      // Navigation must NEVER wait on data: TanStack dedupes a click into any
      // in-flight intent preload, so returning the data warmup here held the
      // old page for the full query waterfall (1.7-3.2s measured). The shell
      // (code chunk) is the only thing a preload may gate on; the warmup
      // races in the background and React Query dedupes it on mount.
      const moduleTask = import("~/features/symbol");
      moduleTask
        .then(({ preloadSymbolRoute }) =>
          preloadSymbolRoute(context, { ...params, assetClass }),
        )
        .catch(() => {
          // Warmup is best-effort; the page's own queries surface real errors.
        });

      if (preload) {
        return moduleTask.then(() => undefined);
      }
    },
  };
}
